import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import ToolSelector from '../../components/ToolSelector';
import PageTutorial from '../../components/PageTutorial';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
 Users, Search, Ban, Trash2, Clock,
 AlertCircle, CheckCircle, ArrowLeft, UserCheck, Leaf, ShieldCheck,
 Shield, ArrowUp, ArrowDown, Sparkles, ShieldAlert
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import ModerationActionModal from '../../components/admin/ModerationActionModal';
import AdminUserFormModal from '../../components/admin/AdminUserFormModal';

const UserManagement = () => {
 const { API } = useContext(AuthContext);
 const navigate = useNavigate();
 const [users, setUsers] = useState([]);
 const [filteredUsers, setFilteredUsers] = useState([]);
 const [loading, setLoading] = useState(true);
 const [searchTerm, setSearchTerm] = useState('');
 const [filterStatus, setFilterStatus] = useState('all');
 const [selectedUser, setSelectedUser] = useState(null);
 const [showActionModal, setShowActionModal] = useState(false);
 const [actionType, setActionType] = useState('');
 const [actionReason, setActionReason] = useState('');
 const [suspendDays, setSuspendDays] = useState(7);
 const [modActionUser, setModActionUser] = useState(null);
 const [showCreateUser, setShowCreateUser] = useState(false);

 useEffect(() => { fetchUsers(); }, []);
 useEffect(() => { filterUsers(); /* eslint-disable-next-line */ }, [searchTerm, filterStatus, users]);

 const fetchUsers = async () => {
 try {
 const response = await axios.get(`${API}/admin/users`);
 const list = Array.isArray(response.data) ? response.data : (response.data.users || []);
 setUsers(list);
 } catch (error) {
 console.error('Error fetching users:', error);
 toast.error('Failed to load users');
 } finally {
 setLoading(false);
 }
 };

 const filterUsers = () => {
 let filtered = users;
 if (filterStatus !== 'all') {
 filtered = filtered.filter(u => {
 if (filterStatus === 'active') return u.status === 'active' || !u.status;
 if (filterStatus === 'suspended') return u.status === 'suspended';
 if (filterStatus === 'banned') return u.status === 'banned';
 return true;
 });
 }
 if (searchTerm) {
 const q = searchTerm.toLowerCase();
 filtered = filtered.filter(u =>
 u.email?.toLowerCase().includes(q) ||
 u.first_name?.toLowerCase().includes(q) ||
 u.last_name?.toLowerCase().includes(q) ||
 u.id?.toLowerCase().includes(q)
 );
 }
 setFilteredUsers(filtered);
 };

 const openActionModal = (user, action) => {
 setSelectedUser(user);
 setActionType(action);
 setActionReason('');
 setSuspendDays(7);
 setShowActionModal(true);
 };
 const closeActionModal = () => {
 setShowActionModal(false);
 setSelectedUser(null);
 setActionType('');
 setActionReason('');
 };

 const executeAction = async () => {
 if (!actionReason.trim()) {
 toast.error('Please provide a reason for this action');
 return;
 }
 try {
 if (actionType === 'promote' || actionType === 'demote') {
 const newRole = actionType === 'promote' ? 'admin' : 'free';
 await axios.put(`${API}/admin/users/${selectedUser.id}/role`, {
 role: newRole,
 reason: actionReason,
 });
 toast.success(`User ${actionType === 'promote' ? 'promoted to admin' : 'demoted from admin'}`);
 closeActionModal();
 fetchUsers();
 return;
 }
 let endpoint = '';
 const payload = { user_id: selectedUser.id, reason: actionReason };
 switch (actionType) {
 case 'suspend': endpoint = '/admin/users/suspend'; payload.days = suspendDays; break;
 case 'ban': endpoint = '/admin/users/ban'; break;
 case 'delete': endpoint = '/admin/users/delete'; break;
 case 'unban': endpoint = '/admin/users/unban'; break;
 default: return;
 }
 await axios.post(`${API}${endpoint}`, payload);
 toast.success(`User ${actionType}${actionType.endsWith('e') ? 'd' : 'ed'} successfully`);
 closeActionModal();
 fetchUsers();
 } catch (error) {
 console.error('Action error:', error);
 toast.error(error.response?.data?.detail || 'Action failed');
 }
 };

 // Inline toggle for the `is_expert` flag — no confirmation modal, since
 // it's reversible and does not change tier/role. Members flagged as
 // experts show up in Ask-an-Expert's online roster.
 const toggleExpert = async (user) => {
 const next = !user.is_expert;
 try {
 await axios.put(`${API}/admin/users/${user.id}/expert-flag`, {
 is_expert: next,
 reason: next
 ? 'Flagged as domain expert via User Management'
 : 'Un-flagged as domain expert via User Management',
 });
 toast.success(next ? 'Marked as expert' : 'Expert flag removed');
 setUsers((prev) => prev.map((u) => (u.id === user.id ? { ...u, is_expert: next } : u)));
 } catch (error) {
 console.error('Expert flag error:', error);
 toast.error(error.response?.data?.detail || 'Could not update expert flag');
 }
 };

 const statusPill = (user) => {
 const status = user.status || 'active';
 const map = {
 active: { cls: 'status-pill status-pill-active', icon: CheckCircle, label: 'Active' },
 suspended: { cls: 'status-pill status-pill-suspended', icon: Clock, label: 'Suspended' },
 banned: { cls: 'status-pill status-pill-banned', icon: Ban, label: 'Banned' },
 };
 const m = map[status] || map.active;
 const Icon = m.icon;
 return (
 <span className={m.cls} data-testid={`user-status-${status}`}>
 <Icon className="w-3 h-3" /> {m.label}
 </span>
 );
 };

 if (loading) {
 return (
 <div className="min-h-screen bg-background flex items-center justify-center" data-testid="usermgmt-loading">
 <div className="flex flex-col items-center gap-4">
 <div className="h-10 w-10 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
 <p className="text-muted-foreground">Loading community…</p>
 </div>
 </div>
 );
 }

 const counts = {
 all: users.length,
 active: users.filter(u => !u.status || u.status === 'active').length,
 suspended: users.filter(u => u.status === 'suspended').length,
 banned: users.filter(u => u.status === 'banned').length,
 };

 return (
 <div className="min-h-screen bg-background" data-testid="user-management-page">
 {/* Top nav */}
 <header className="sticky top-0 z-30 bg-background/95 backdrop-blur border-b border-border">
 <div className="container mx-auto px-6 h-16 flex items-center justify-between">
 <div className="flex items-center gap-3">
 <Button
 variant="ghost"
 size="sm"
 onClick={() => navigate('/admin/portal')}
 className="text-muted-foreground hover:text-foreground -ml-3 font-semibold"
 data-testid="back-to-admin-btn"
 >
 <ArrowLeft className="w-4 h-4 mr-2" /> Admin portal
 </Button>
 </div>
 <div className="flex items-center gap-2.5">
 <div className="h-9 w-9 rounded-lg bg-navy text-primary flex items-center justify-center">
 <Leaf className="w-4 h-4 fill-primary" />
 </div>
 <span className="font-heading text-base font-extrabold tracking-tight">Freeze Dry Academy</span>
 </div>
 <div className="w-[140px]" />
 </div>
 </header>

 <ToolSelector />

 <main className="container mx-auto px-6 py-10">
 <PageTutorial id="admin_users" />
 {/* Page header */}
 <section className="mb-10 animate-fade-up">
 <span className="eyebrow flex items-center gap-1.5">
 <ShieldCheck className="w-3 h-3" /> Moderation
 </span>
 <div className="mt-4 flex flex-col lg:flex-row lg:items-end justify-between gap-6">
 <div className="max-w-2xl">
 <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight">
 User <span className="text-amber-brand">management</span>
 </h1>
 <p className="mt-3 text-muted-foreground text-pretty">
 Moderate accounts, handle violations, and keep the community a calm place to learn.
 </p>
 <div className="mt-4 flex flex-wrap gap-2">
 <Button
 onClick={() => setShowCreateUser(true)}
 className="rounded-full font-bold"
 data-testid="create-user-cta"
 >
 <UserCheck className="w-4 h-4 mr-2" /> Create user
 </Button>
 <Button
 onClick={() => navigate('/admin/invites')}
 variant="outline"
 className="rounded-full font-bold"
 data-testid="admin-invite-cta"
 >
 <ShieldCheck className="w-4 h-4 mr-2" /> Invite an admin or moderator
 </Button>
 <Button
 onClick={() => navigate('/admin/mod-room')}
 variant="outline"
 className="rounded-full font-bold"
 data-testid="mod-room-cta"
 >
 <Shield className="w-4 h-4 mr-2" /> Mod Room
 </Button>
 </div>
 </div>
 <div className="grid grid-cols-4 gap-2 lg:gap-3">
 {[
 { k: 'all', label: 'Total', v: counts.all },
 { k: 'active', label: 'Active', v: counts.active },
 { k: 'suspended', label: 'Suspended', v: counts.suspended },
 { k: 'banned', label: 'Banned', v: counts.banned },
 ].map(s => (
 <button
 key={s.k}
 onClick={() => setFilterStatus(s.k)}
 className={`pro-card p-3 lg:p-4 text-left transition ${
 filterStatus === s.k ? 'ring-2 ring-primary ring-offset-2 ring-offset-background' : 'hover:bg-muted/40'
 }`}
 data-testid={`stat-${s.k}-btn`}
 >
 <div className="font-heading text-2xl lg:text-3xl font-extrabold">{s.v}</div>
 <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1 font-semibold">{s.label}</div>
 </button>
 ))}
 </div>
 </div>
 </section>

 {/* Search bar */}
 <div className="pro-card p-4 sm:p-5 mb-8 flex flex-col sm:flex-row gap-3">
 <div className="relative flex-1">
 <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
 <Input
 placeholder="Search by email, name, or ID…"
 value={searchTerm}
 onChange={(e) => setSearchTerm(e.target.value)}
 className="pl-10 h-11 bg-transparent border-border"
 data-testid="user-search-input"
 />
 </div>
 <div className="flex items-center gap-1 bg-muted/60 p-1 rounded-full" data-testid="status-filter">
 {[
 { k: 'all', label: 'All' },
 { k: 'active', label: 'Active' },
 { k: 'suspended', label: 'Suspended' },
 { k: 'banned', label: 'Banned' },
 ].map(t => (
 <button
 key={t.k}
 onClick={() => setFilterStatus(t.k)}
 data-testid={`filter-${t.k}-btn`}
 className={`px-3.5 py-1.5 rounded-full text-xs font-medium transition ${
 filterStatus === t.k
 ? 'bg-background text-foreground shadow-sm'
 : 'text-muted-foreground hover:text-foreground'
 }`}
 >
 {t.label}
 </button>
 ))}
 </div>
 </div>

 {/* User list */}
 {filteredUsers.length === 0 ? (
 <div className="pro-card p-12 text-center" data-testid="no-users">
 <Users className="w-12 h-12 mx-auto mb-4 text-muted-foreground/60" />
 <p className="font-heading text-2xl font-bold">No users found</p>
 <p className="text-sm text-muted-foreground mt-1">Try adjusting your search or filters.</p>
 </div>
 ) : (
 <div className="space-y-3 stagger">
 {filteredUsers.map((user, idx) => (
 <div
 key={user.id}
 className="pro-card p-5 flex flex-col md:flex-row md:items-center gap-4 animate-fade-up"
 style={{ '--i': Math.min(idx, 10) }}
 data-testid={`user-row-${user.id}`}
 >
 <div className="flex items-center gap-4 flex-1 min-w-0">
 <div className="h-12 w-12 rounded-xl bg-navy text-primary border border-border flex items-center justify-center font-heading text-xl font-extrabold flex-shrink-0">
 {user.email?.[0]?.toUpperCase() || '?'}
 </div>
 <div className="min-w-0">
 <div className="flex items-center gap-2 flex-wrap">
 <h3 className="font-heading text-base font-bold truncate">
 <button
 type="button"
 onClick={() => navigate(`/admin/users/${user.id}`)}
 className="hover:underline text-left"
 data-testid={`user-view-detail-${user.id}`}
 >
 {user.email}
 </button>
 </h3>
 {statusPill(user)}
 {user.role === 'admin' && (
 <span className="status-pill status-pill-admin">
 <ShieldCheck className="w-3 h-3" /> Admin
 </span>
 )}
 {user.is_expert && (
 <span
 className="inline-flex items-center gap-1 rounded-full border border-primary/40 bg-primary/10 text-primary px-2 h-5 text-[10px] uppercase tracking-widest font-bold"
 data-testid={`user-expert-pill-${user.id}`}
 title="Appears in Ask-an-Expert online roster"
 >
 <Sparkles className="w-3 h-3" /> Expert
 </span>
 )}
 </div>
 <div className="text-xs text-muted-foreground mt-1 font-mono">
 ID {user.id?.slice(0, 8)} · Joined {new Date(user.created_at || Date.now()).toLocaleDateString()}
 </div>
 {user.suspension_until && new Date(user.suspension_until) > new Date() && (
 <div className="text-xs text-amber-700 dark:text-amber-300 mt-1">
 Suspended until {new Date(user.suspension_until).toLocaleDateString()}
 </div>
 )}
 {user.ban_reason && (
 <div className="text-xs text-destructive mt-1 truncate">
 Reason: {user.ban_reason}
 </div>
 )}
 </div>
 </div>

 {user.role !== 'admin' ? (
 <div className="flex flex-wrap gap-2 md:justify-end" data-testid={`user-actions-${user.id}`}>
 {(!user.status || user.status === 'active') ? (
 <>
 <Button
 size="sm"
 variant="outline"
 onClick={() => openActionModal(user, 'promote')}
 className="rounded-full text-primary border-primary/40 hover:bg-primary/5 font-semibold"
 data-testid={`promote-btn-${user.id}`}
 >
 <Shield className="w-3.5 h-3.5 mr-1.5" /> Promote
 </Button>
 <Button
 size="sm"
 variant="outline"
 onClick={() => setModActionUser(user)}
 className="rounded-full text-rose-700 border-rose-300 hover:bg-rose-50 dark:text-rose-300 dark:border-rose-800 dark:hover:bg-rose-900/20 font-semibold"
 data-testid={`take-action-btn-${user.id}`}
 title="Soft-ban 24h + optional DM + optional public admin reply"
 >
 <ShieldAlert className="w-3.5 h-3.5 mr-1.5" /> Take action…
 </Button>
 <Button
 size="sm"
 variant="outline"
 onClick={() => openActionModal(user, 'suspend')}
 className="rounded-full text-amber-700 border-amber-300 hover:bg-amber-50 dark:text-amber-300 dark:border-amber-800 dark:hover:bg-amber-900/20"
 data-testid={`suspend-btn-${user.id}`}
 >
 <Clock className="w-3.5 h-3.5 mr-1.5" /> Suspend
 </Button>
 <Button
 size="sm"
 variant="outline"
 onClick={() => openActionModal(user, 'ban')}
 className="rounded-full text-destructive border-destructive/30 hover:bg-destructive/5"
 data-testid={`ban-btn-${user.id}`}
 >
 <Ban className="w-3.5 h-3.5 mr-1.5" /> Ban
 </Button>
 </>
 ) : (
 <Button
 size="sm"
 variant="outline"
 onClick={() => openActionModal(user, 'unban')}
 className="rounded-full text-emerald-700 border-emerald-300 hover:bg-emerald-50 dark:text-emerald-300 dark:border-emerald-800 dark:hover:bg-emerald-900/20"
 data-testid={`unban-btn-${user.id}`}
 >
 <UserCheck className="w-3.5 h-3.5 mr-1.5" /> Restore
 </Button>
 )}
 <Button
 size="sm"
 variant="ghost"
 onClick={() => openActionModal(user, 'delete')}
 className="rounded-full text-destructive hover:bg-destructive/10"
 data-testid={`delete-btn-${user.id}`}
 >
 <Trash2 className="w-3.5 h-3.5" />
 </Button>
 <Button
 size="sm"
 variant="outline"
 onClick={() => toggleExpert(user)}
 className={`rounded-full font-semibold ${user.is_expert
 ? 'text-primary border-primary/50 bg-primary/5 hover:bg-primary/10'
 : 'text-muted-foreground border-border hover:bg-muted'}`}
 data-testid={`toggle-expert-btn-${user.id}`}
 title={user.is_expert
 ? 'Remove expert flag (member no longer appears in Ask-an-Expert roster)'
 : 'Flag this member as a domain expert (appears in Ask-an-Expert roster)'}
 >
 <Sparkles className="w-3.5 h-3.5 mr-1.5" />
 {user.is_expert ? 'Un-expert' : 'Mark expert'}
 </Button>
 </div>
 ) : (
 <div className="flex flex-wrap gap-2 md:justify-end" data-testid={`user-actions-${user.id}`}>
 <Button
 size="sm"
 variant="outline"
 onClick={() => openActionModal(user, 'demote')}
 className="rounded-full text-muted-foreground border-border hover:bg-muted font-semibold"
 data-testid={`demote-btn-${user.id}`}
 >
 <ArrowDown className="w-3.5 h-3.5 mr-1.5" /> Demote admin
 </Button>
 <Button
 size="sm"
 variant="outline"
 onClick={() => toggleExpert(user)}
 className={`rounded-full font-semibold ${user.is_expert
 ? 'text-primary border-primary/50 bg-primary/5 hover:bg-primary/10'
 : 'text-muted-foreground border-border hover:bg-muted'}`}
 data-testid={`toggle-expert-btn-${user.id}`}
 title={user.is_expert
 ? 'Remove expert flag (admins are experts by default via role)'
 : 'Also flag this admin explicitly as an expert'}
 >
 <Sparkles className="w-3.5 h-3.5 mr-1.5" />
 {user.is_expert ? 'Un-expert' : 'Mark expert'}
 </Button>
 </div>
 )}
 </div>
 ))}
 </div>
 )}
 </main>

 {/* Action modal */}
 {showActionModal && selectedUser && (
 <div className="fixed inset-0 bg-foreground/40 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="action-modal">
 <Card className="pro-card max-w-lg w-full animate-fade-up border-0">
 <CardHeader>
 <span className="eyebrow">
 {actionType === 'suspend' && 'Temporary action'}
 {actionType === 'ban' && 'Permanent action'}
 {actionType === 'delete' && 'Destructive action'}
 {actionType === 'unban' && 'Restorative action'}
 {actionType === 'promote' && 'Role change'}
 {actionType === 'demote' && 'Role change'}
 </span>
 <CardTitle className="font-heading text-2xl font-bold mt-2 flex items-center gap-2 tracking-tight">
 {actionType === 'suspend' && <><Clock className="w-5 h-5 text-amber-600" /> Suspend user</>}
 {actionType === 'ban' && <><Ban className="w-5 h-5 text-destructive" /> Ban user</>}
 {actionType === 'delete' && <><Trash2 className="w-5 h-5 text-destructive" /> Delete account</>}
 {actionType === 'unban' && <><UserCheck className="w-5 h-5 text-emerald-600" /> Restore access</>}
 {actionType === 'promote' && <><ArrowUp className="w-5 h-5 text-primary" /> Promote to admin</>}
 {actionType === 'demote' && <><ArrowDown className="w-5 h-5 text-muted-foreground" /> Demote from admin</>}
 </CardTitle>
 <CardDescription>
 Target: <strong className="text-foreground">{selectedUser.email}</strong>
 </CardDescription>
 </CardHeader>
 <CardContent className="space-y-5">
 {actionType === 'suspend' && (
 <div className="space-y-2">
 <Label className="text-xs uppercase tracking-wider text-muted-foreground">Suspension duration</Label>
 <div className="flex flex-wrap gap-2">
 {[1, 3, 7, 14, 30, 90].map(d => (
 <button
 key={d}
 type="button"
 onClick={() => setSuspendDays(d)}
 className={`px-3.5 py-2 rounded-full text-xs font-medium border transition ${
 suspendDays === d
 ? 'bg-primary text-primary-foreground border-primary'
 : 'bg-transparent border-border text-foreground/80 hover:bg-muted'
 }`}
 data-testid={`suspend-${d}d-btn`}
 >
 {d} {d === 1 ? 'day' : 'days'}
 </button>
 ))}
 </div>
 </div>
 )}

 <div className="space-y-2">
 <Label className="text-xs uppercase tracking-wider text-muted-foreground">Reason *</Label>
 <textarea
 value={actionReason}
 onChange={(e) => setActionReason(e.target.value)}
 placeholder={
 actionType === 'suspend' ? 'Spam posting, harassment, policy violation…' :
 actionType === 'ban' ? 'Repeated violations, serious misconduct…' :
 actionType === 'delete' ? 'User request, GDPR compliance…' :
 'Warning resolved, good behavior…'
 }
 className="w-full p-3 border border-border rounded-xl bg-transparent min-h-[110px] focus:outline-none focus:ring-2 focus:ring-primary/30"
 data-testid="action-reason-input"
 />
 </div>

 {actionType !== 'unban' && actionType !== 'promote' && actionType !== 'demote' && (
 <div className="rounded-xl border border-destructive/20 bg-destructive/5 p-4">
 <div className="flex items-start gap-3">
 <AlertCircle className="w-4 h-4 text-destructive flex-shrink-0 mt-0.5" />
 <p className="text-sm text-destructive/90 leading-relaxed">
 {actionType === 'suspend' && <><strong>Temporary lockout.</strong> User can log in again once suspension ends.</>}
 {actionType === 'ban' && <><strong>Permanent block.</strong> Reversible later from this page.</>}
 {actionType === 'delete' && <><strong>This cannot be undone.</strong> All associated data is removed.</>}
 </p>
 </div>
 </div>
 )}

 <div className="flex gap-3 pt-2">
 <Button
 onClick={executeAction}
 className={`rounded-full px-6 ${
 actionType === 'unban' ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
 : actionType === 'suspend' ? 'bg-amber-600 hover:bg-amber-700 text-white'
 : actionType === 'promote' || actionType === 'demote' ? 'bg-primary text-primary-foreground hover:bg-primary/90'
 : 'bg-destructive hover:bg-destructive/90'
 }`}
 data-testid="confirm-action-btn"
 >
 Confirm {actionType}
 </Button>
 <Button variant="outline" onClick={closeActionModal} className="rounded-full" data-testid="cancel-action-btn">
 Cancel
 </Button>
 </div>
 </CardContent>
 </Card>
 </div>
 )}

 {modActionUser && (
 <ModerationActionModal
 open={!!modActionUser}
 onClose={() => setModActionUser(null)}
 targetUser={{
 id:           modActionUser.id,
 email:        modActionUser.email,
 display_name: modActionUser.display_name,
 role:         modActionUser.role,
 }}
 onDone={() => { setModActionUser(null); fetchUsers(); }}
 />
 )}
 {showCreateUser && (
 <AdminUserFormModal
 open={showCreateUser}
 mode="create"
 onClose={() => setShowCreateUser(false)}
 onDone={() => { setShowCreateUser(false); fetchUsers(); }}
 />
 )}
 </div>
 );
};

export default UserManagement;
